import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isUiRedesignPageEnabled } from '../../config/uiRedesign';
import { LoginButton } from '../auth/LoginButton';

const v2TeacherPageKey = (pathname: string): string | null => {
  if (/^\/v2\/teacher\/run\/[^/]+(?:\/ops)?\/?$/.test(pathname)) return 'v2_teacher_lesson_control';
  if (/^\/v2\/teacher\/lesson-plan(?:\/[^/]+\/edit)?\/?$/.test(pathname)) return 'v2_teacher_lesson_plan_editor';
  if (/^\/v2\/teacher\/plan(?:\/|$)/.test(pathname)) return 'v2_teacher_plan';
  if (/^\/v2\/teacher\/students(?:\/|$)/.test(pathname) || /^\/v2\/teacher\/rollover\/?$/.test(pathname)) return 'v2_teacher_students';
  if (/^\/v2\/teacher\/goals\/?$/.test(pathname)) return 'v2_teacher_goals';
  if (/^\/v2\/teacher\/reports\/?$/.test(pathname)) return 'v2_teacher_reports';
  if (/^\/v2\/teacher\/mastery(?:\/|$)/.test(pathname)) return 'v2_teacher_mastery_overview';
  if (/^\/v2\/teacher\/review\/?$/.test(pathname)) return 'v2_teacher_review';
  if (/^\/v2\/teacher\/(?:queue|assignments)(?:\/|$)/.test(pathname)) return 'v2_teacher_queue';
  if (/^\/v2\/teacher\/daily-completion\/?$/.test(pathname)) return 'daily-completion';
  if (/^\/v2\/teacher(?:\/section(?:\/|$)|\/section-archives\/?$|\/?$)/.test(pathname)) return 'v2_teacher_sections';
  return null;
};

export const shouldRenderLegacyHeader = (pathname: string): boolean => {
  if (/^\/v2\/student\/classroom\/[^/]+\/?$/.test(pathname)) return false;
  if (/^\/v2\/run\/[^/]+\/?$/.test(pathname)) return !isUiRedesignPageEnabled('v2_student_run_presentation');
  if (/^\/v2\/assignments\/[^/]+\/?$/.test(pathname)) return !isUiRedesignPageEnabled('v2_student_assignment_detail');
  if (/^\/v2\/student\/?$/.test(pathname)) return !isUiRedesignPageEnabled('v2_student_dashboard');
  if (/^\/v2\/student\/run\/[^/]+\/review\/?$/.test(pathname)) return !isUiRedesignPageEnabled('v2_student_run_review');
  if (/^\/v2\/student\/evidence\/[^/]+\/?$/.test(pathname)) return !isUiRedesignPageEnabled('v2_student_evidence_ledger');
  if (/^\/v2\/student\/(?:coaching|standards|reviews\/[^/]+|work-review|units)\/?$/.test(pathname)) return false;
  if (/^\/v2\/teacher\/(?:coaching|prepare|pathways|planning|unit-day-review|assessments|reporting-cycles)(?:\/|$)/.test(pathname)) return false;
  if (/^\/v2\/teacher\/?$/.test(pathname)) return false;
  if (/^\/v2\/teacher\/teach\/?$/.test(pathname)) return false;
  if (/^\/v2\/teacher\/run\/[^/]+\/display\/?$/.test(pathname)) return false;
  if (/^\/v2\/teacher\/classroom\/[^/]+\/(?:display|studio)\/?$/.test(pathname)) return false;
  const pageKey = v2TeacherPageKey(pathname);
  return pageKey ? !isUiRedesignPageEnabled(pageKey) : true;
};

export const Header: React.FC = () => {
  const { user, logout } = useAuth();

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <h1 className="text-xl font-semibold text-gray-900">
              Hibbardsclass
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-gray-500 capitalize">{user.role}</p>
                  </div>
                  <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.first_name[0]}{user.last_name[0]}
                    </span>
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="btn-secondary text-sm"
                >
                  Sign out
                </button>
              </>
            ) : (
              <LoginButton />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export const RouteAwareHeader: React.FC = () => {
  const location = useLocation();
  return shouldRenderLegacyHeader(location.pathname) ? <Header /> : null;
};
